import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import clsx from "clsx";
// Api
import { getCommissionSupportTeamSystemSettings } from "@/Washouts/api/common/queries";
// Hooks
import { useManagedPositions } from "@/Common/hooks/Positions";
// MUI
import { Divider, Grid, Typography } from "@mui/material";
// Helpers
import { errorLogger } from "@/Common/helpers/logger";
// Custom components
import { createErrorToast } from "@/Washouts/components/tables/Common/ChangeRequestTable/helpers";
import { ChangeRequestContainer } from "@/Washouts/components/ChangeRequestContainer";
import { SupportCenterSkeleton } from "./SupportCenterSkeleton";
import { ReviewerCard } from "@/Washouts/components/CommissionCycle/CommissionCycleClosingReviewers/ReviewerCard";
// Types
import { RootState } from "@/redux/store";
import { IPayrollClosingSideBarSelected } from "@/Washouts/types/PayrollClosing/PayrollClosingSideBar";
import { ICommunication } from "@/Common/interfaces/user";
import { IReviewerStatus } from "@/Washouts/types/PayrollClosing/CommissionCycleClosingReviewers";

const getCommunicationValues = (communications: ICommunication[] = []) => {
	const phone = communications?.find((item) => {
		return item.communicationType === "PHONE" && item.usage === "BUSINESS";
	});

	const email = communications?.find((item) => {
		return item.communicationType === "EMAIL" && item.usage === "BUSINESS";
	});

	return { phone, email };
};

type NullableProperties<T> = {
	[P in keyof T]: T[P] | null;
};

const buildReviewer = (reviewer: Partial<NullableProperties<IReviewerStatus & { _id: string }>>): IReviewerStatus & { _id: string } => {
	return {
		_id: reviewer._id ?? "",
		picturePath: reviewer.picturePath ?? "Holder",
		name: reviewer.name ?? "",
		title: reviewer.title ?? "",
		email: reviewer.email ?? "Unknown",
		phone: reviewer.phone ?? "Unknown",
		status: reviewer.status ?? "",
		position: reviewer.position ?? "",
	};
};

interface ISupportCenterContainerProps {
	selectedItem: IPayrollClosingSideBarSelected | null;
}

export const SupportCenterContent = ({ selectedItem }: Readonly<ISupportCenterContainerProps>) => {
	const user = useSelector((state: RootState) => state.user.user);
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const { enqueueSnackbar } = useSnackbar();
	const [showOnlyPending, setShowOnlyPending] = useState(true);
	const [selectedPositions, setSelectedPositions] = useState<string[]>([]);

	const { data: managedPositions, isLoading: loadingPositions } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const { data: supportTeam, isLoading: loadingSupportTeam } = useQuery({
		queryKey: ["commissionSupportTeam", locationId],
		queryFn: async () => {
			if (!locationId || !managedPositions?.length) {
				throw new Error("No locationId or managedPositions");
			}
			const team: (IReviewerStatus & { _id: string })[] = [];
			const ids: { id: string; position: string }[] = [];

			try {
				const result = await getCommissionSupportTeamSystemSettings(locationId);

				result?.commissionSupportTeam?.forEach((member) => {
					const communication = getCommunicationValues(member.communications);
					team.push(
						buildReviewer({
							_id: member._id,
							picturePath: member.presignedProfilePicture,
							name: member.fullName,
							title: member.position,
							email: communication?.email?.value,
							phone: communication?.phone?.value,
							status: "Support for all positions",
							position: "All Positions",
						})
					);
				});
				const partOfCommissionSupportTeam = team.some((item) => item._id === user?._id);

				result?.washoutReviewers?.forEach((item) => {
					const alreadyAdded = team.find((member) => member._id === item.reviewerId._id && member.position === "All Positions");
					if (!alreadyAdded) {
						const reviewer = item.reviewerId;
						const communication = getCommunicationValues(reviewer.communications);
						const found = managedPositions.includes(item.position);
						if (found) {
							team.push(
								buildReviewer({
									_id: reviewer._id,
									picturePath: reviewer.presignedProfilePicture,
									name: reviewer.fullName,
									title: reviewer.position,
									email: communication?.email?.value,
									phone: communication?.phone?.value ?? "Unknown",
									status: `Support for ${item.position}`,
									position: item.position,
								})
							);
							ids.push({ id: item._id, position: item.position });
						}
					}
				});

				return { ids, data: team, commissionSupportTeam: partOfCommissionSupportTeam };
			} catch (error) {
				errorLogger(error as Error, { message: "An error occurred retrieving the commission support team." });
				createErrorToast(enqueueSnackbar, "An error occurred retrieving the commission support team.");
			}

			return null;
		},
		staleTime: Infinity,
		enabled: !!locationId && !!managedPositions?.length,
	});

	useEffect(() => {
		if (!location) {
			return () => {};
		}

		return () => {
			window.history.replaceState({}, document.title);
		};
	}, [location]);

	if (!selectedItem || !locationId) {
		return null;
	}

	if (loadingPositions || loadingSupportTeam) {
		return <SupportCenterSkeleton />;
	}

	return (
		<Grid container className="!w-full">
			<Grid item xs={12}>
				<Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
					<Grid item>
						<Typography className="!font-montserrat !font-medium !text-2xl/[1.2] text-default" component="div">
							Commission Support Team
						</Typography>
					</Grid>
				</Grid>
			</Grid>
			<Grid
				item
				xs={12}
				className={clsx("!my-9 pt-1 !pb-5 pl-5 relative max-h-[460px] overflow-auto", {
					"!pr-4": (supportTeam?.data.length ?? 0) > 9,
				})}
			>
				<Grid container className="flex-row" spacing={5.5}>
					{supportTeam?.data.map((reviewer, idx) => {
						const key = `${reviewer.title}_${idx}`;
						return <ReviewerCard reviewer={reviewer} key={key} />;
					})}
				</Grid>
			</Grid>
			<Grid item xs={12} className="pt-10">
				<Divider />
			</Grid>
			<Grid item xs={12} className="pt-10">
				<Grid item>
					<Typography className="!font-montserrat !font-medium !text-2xl/[1.2] text-default" component="div">
						Change Requests
					</Typography>
				</Grid>
			</Grid>
			<Grid item xs={12} className="py-5">
				<ChangeRequestContainer
					key={selectedItem._id}
					locationId={locationId}
					payPeriodId={selectedItem?.payPeriodId}
					escalated
					hideOpenWashout={false}
					disableStatusAction={false}
					hideEscalateAction
					tableProps={{
						switchValue: "PENDING",
						initialSwitchState: showOnlyPending,
						onSwitchChange: setShowOnlyPending,
						showPositionsSelector: true,
						initialSelectedPositions: selectedPositions,
						onSelectedPositionsChange: setSelectedPositions,
					}}
				/>
			</Grid>
		</Grid>
	);
};
